import React, { useEffect, useState } from 'react'
import { useExpanded, useTable } from 'react-table'
import { useTranslation } from 'react-i18next'
import { compareAsc, parseISO } from 'date-fns'
import DocumentService from 'routes/Declaration/services/document.service'
import LoadingBackdrop from 'components/LoadingBackdrop'
import { dateFormatWithTimeOptions } from 'config/dateFormatterConfig'
import { useFetchTransitOperationLogs } from 'features/declaration/event-log/api/fetchTransitOperationLogs'
import { fetchSubmitMessageLog } from 'features/declaration/event-log/api/fetchSubmitMessageLog'
import { TransitOperationLog } from 'features/declaration/event-log/models/types'
import { EventLogStatus, messageCodeOf, resolveEventLogStatus } from 'features/declaration/event-log/models/messageLogSignals'
import { extractFileNameFromContentPositionHeader } from 'helpers/axios-header.helper'
import { NctsCountry } from 'config'
import LogDetailsRow from './LogDetailsRow'

interface EventLogRow {
  id: number | undefined
  date: string
  type: string
  message: string | undefined
  status: EventLogStatus
  created?: Date
}

const mapToRows = (
  eventLogs: TransitOperationLog[],
  country: NctsCountry | null | undefined,
): EventLogRow[] => eventLogs.map((log: TransitOperationLog) => {
  const { messageContext, created } = log

  const status = resolveEventLogStatus(messageCodeOf(messageContext, country) ?? '')

  let date = ''
  if (created !== undefined) {
    date = new Intl.DateTimeFormat('et', dateFormatWithTimeOptions).format(new Date(created))
  }

  return (
    {
      id: log.id,
      status,
      date,
      created,
      type: 'form',
      message: messageContext,
    })
}).sort((a, b) => {
  if (a.created === undefined || b.created === undefined) {
    return 0
  }
  // Logs from one poll share a timestamp; the id keeps them in the order they were written.
  return compareAsc(parseISO(`${a.created}`), parseISO(`${b.created}`)) || (a.id ?? 0) - (b.id ?? 0)
})

const SUBMIT_MESSAGE_TYPES = ['IE015', 'IEA15', 'FI015A', 'IE3F50', 'lt.ppmks.IEG347']

const isSubmitMessage = (
  messageType: string | undefined,
): boolean => SUBMIT_MESSAGE_TYPES.some((
  submitMessageType,
) => messageType?.includes(submitMessageType))

function isPolishTransitEndedMessage(type: string, currentCountry: NctsCountry | null | undefined): boolean {
  return currentCountry === 'PL' && type === 'IE045'
}

interface EventLogProps {
  transitOperationId: number | null | undefined
  country: NctsCountry | null | undefined
}

function EventLog({ transitOperationId, country }: Readonly<EventLogProps>) {
  const { data: transitOperationLogs, isLoading } = useFetchTransitOperationLogs(transitOperationId ?? null)
  const { t } = useTranslation()

  const [logs, setLogs] = useState<EventLogRow[]>([])

  const downloadSubmitMessage = async (log: { id: number; message: string }) => {
    const response = await fetchSubmitMessageLog({
      logId: log.id,
      messageType: log.message,
    })

    const { data: responseData } = response
    const filename = extractFileNameFromContentPositionHeader(response)
    DocumentService.saveBlob(null, responseData, 'blob', filename)
  }

  useEffect(() => {
    if (transitOperationLogs) {
      setLogs(mapToRows(transitOperationLogs, country))
    }
  }, [transitOperationLogs, country])

  const columns = React.useMemo(() => [
    {
      Header: 'Date',
      accessor: 'date',
    },
    {
      Header: 'Type',
      accessor: 'type',
    },
    {
      Header: 'Message',
      accessor: 'message',
    },
  ], [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    /* eslint-disable  @typescript-eslint/no-explicit-any */
  } = useTable<any>({ columns, data: logs }, useExpanded)

  return (
    <div className="container-fluid py-3">
      {isLoading && (
        <div>
          <LoadingBackdrop loading isInTable={false} />
        </div>
      )}
      <table
        className="table table-sm table-borderless data-table me-2"
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
          /* eslint-disable-next-line react/jsx-props-no-spreading */
            <tr {...headerGroup.getHeaderGroupProps()} key={`${headerGroup.id} ${headerGroup.columns?.length}`}>
              <th className="log-status" aria-label="status" />
              {headerGroup.headers.map((column) => (
                <th
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...column.getHeaderProps()}
                  key={column.id}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)

            return (
              <React.Fragment key={row.id + row.cells.length}>
                {(
                  <tr className={` ${row.id}`} {...row.getRowProps()}>
                    <td className="log-status">
                      {
                      // eslint-disable-next-line no-nested-ternary
                        (row.original.status === 'ACCEPTED')
                          ? <i className="fal fa-check-circle text-success fa-md me-1" />
                          : null
                      }
                      {(row.original.status === 'REFUSED')
                        ? <i className="fal fa-exclamation-triangle text-danger fa-md me-1" />
                        : ''}
                    </td>
                    {row.cells.map((cell) => (
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                      <td {...cell.getCellProps()}>
                        {cell.column.Header === 'Message'
                          ? (
                            <>
                              <button
                                className="btn btn-link border-0 p-0 me-2 text-nowrap"
                                type="button"
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...row.getToggleRowExpandedProps()}
                              >
                                {cell.render('Cell')}
                              </button>
                              {(isSubmitMessage(row.original.message) || isPolishTransitEndedMessage(row.original.message, country)) && (
                                <button
                                  type="button"
                                  onClick={() => downloadSubmitMessage(row.original)}
                                >
                                  {t('buttons.downloadXml')}
                                </button>
                              )}
                            </>
                          )
                          : cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )}
                {row.isExpanded && (
                  <LogDetailsRow logId={row.original.id} messageContext={row.original.message} colSpan={row.cells.length + 1} />
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default EventLog
